import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconCheckWhiteModalCourses,
  iconCloseModalCourses,
  iconTranslateStepCourses,
  iconVideoModalCourses,
  iconVideoSmallModalCourses,
} from '../../assets';
import { useTheme } from '../../theme';
import type { LangKey } from './ModuleEditor/LanguageTabs';
import { LanguageTabs } from './ModuleEditor/LanguageTabs';

const defaultName = (): Record<LangKey, string> => ({ en: '', es: '' });

/** Extrai o ID do vídeo de uma URL do Vimeo (vimeo.com/123, player.vimeo.com/video/123, etc). */
function getVimeoVideoId(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const patterns = [
      /vimeo\.com\/(?:video\/)?(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
    ];
    for (const re of patterns) {
      const m = trimmed.match(re);
      if (m) return m[1];
    }
    return null;
  } catch {
    return null;
  }
}

export interface VideoLessonModalProps {
  open: boolean;
  onClose: () => void;
  /** Pode retornar Promise para salvar no backend (ex.: edição); o modal mostra loading até concluir. */
  onConfirm: (vimeoUrl: string, name: Record<LangKey, string>) => void | Promise<void>;
  initialVimeoUrl?: string;
  initialName?: Record<LangKey, string>;
  /** Quando true, o botão de confirmação exibe texto de "salvar" em vez de "adicionar". */
  isEditMode?: boolean;
}

export function VideoLessonModal({
  open,
  onClose,
  onConfirm,
  initialVimeoUrl = '',
  initialName,
  isEditMode = false,
}: VideoLessonModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [vimeoUrl, setVimeoUrl] = useState(initialVimeoUrl);
  const [name, setName] = useState<Record<LangKey, string>>(defaultName);
  const [nameLang, setNameLang] = useState<LangKey>('en');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const vimeoVideoId = useMemo(() => getVimeoVideoId(vimeoUrl), [vimeoUrl]);

  useEffect(() => {
    if (open) {
      setVimeoUrl(initialVimeoUrl);
      setName(initialName ?? defaultName());
    }
  }, [open, initialVimeoUrl, initialName]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  const handleNameChange = useCallback((lang: LangKey, value: string) => {
    setName(prev => ({ ...prev, [lang]: value }));
  }, []);

  const handleConfirm = useCallback(async () => {
    const trimmed = vimeoUrl.trim();
    if (!trimmed) return;
    setIsSubmitting(true);
    try {
      await onConfirm(trimmed, name);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [vimeoUrl, name, onConfirm, onClose]);

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: theme.palette.createCourse.modalOverlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: theme.spacing.lg,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.lg,
    boxShadow:
      theme.palette.createCourse.modalShadow,
    width: '100%',
    maxWidth: 480,
    maxHeight: '90vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    paddingBottom: 0,
    flexShrink: 0,
  };

  const headerLeftStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: theme.spacing.xs,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius.sm,
    lineHeight: 1,
  };

  const bodyStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    flex: 1,
    minHeight: 0,
  };

  const labelRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
  };

  const requiredAsteriskStyle: React.CSSProperties = {
    color: theme.palette.error.main,
  };

  const tabsWrapStyle: React.CSSProperties = {
    marginBottom: theme.spacing.sm,
  };

  const translateIconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    objectFit: 'contain',
    flexShrink: 0,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: `${theme.spacing.inputY} ${theme.spacing.inputX}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.input.background,
    border: 'none',
    borderRadius: theme.shape.borderRadius.sm,
    boxSizing: 'border-box',
    outline: 'none',
    marginBottom: theme.spacing.lg,
  };

  const previewBoxStyle: React.CSSProperties = {
    backgroundColor: theme.palette.input.background,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    padding: vimeoVideoId ? theme.spacing.xs : theme.spacing.lg,
    overflow: 'hidden',
  };

  const previewIframeStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '16 / 9',
    border: 'none',
    borderRadius: theme.shape.borderRadius.sm,
    flex: 1,
    minHeight: 0,
  };

  const previewTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const previewHintStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.text.secondary,
    margin: 0,
    textAlign: 'center',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing.sm,
    padding: theme.spacing.lg,
    paddingTop: 0,
    flexShrink: 0,
  };

  const cancelButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    cursor: 'pointer',
  };

  const confirmButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    borderRadius: theme.shape.borderRadius.sm,
    cursor: 'pointer',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-lesson-modal-title"
      style={overlayStyle}
      onKeyDown={handleKeyDown}
    >
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <header style={headerStyle}>
          <div style={headerLeftStyle}>
            <img
              src={iconVideoModalCourses}
              alt=""
              width={24}
              height={24}
              style={{
                objectFit: 'contain',
                flexShrink: 0,
                filter: 'brightness(0)',
              }}
              aria-hidden
            />
            <h2 id="video-lesson-modal-title" style={titleStyle}>
              {t('courses.create.configure_video_lesson')}
            </h2>
          </div>
          <button
            type="button"
            style={closeButtonStyle}
            onClick={onClose}
            aria-label={t('courses.create.cancel')}
          >
            <img
              src={iconCloseModalCourses}
              alt=""
              width={20}
              height={20}
              style={{ objectFit: 'contain' }}
              aria-hidden
            />
          </button>
        </header>

        <div style={bodyStyle}>
          <div style={labelRowStyle}>
            <span style={labelStyle}>
              {t('courses.create.lesson_name')}
            </span>
            <img
              src={iconTranslateStepCourses}
              alt=""
              style={translateIconStyle}
              aria-hidden
            />
          </div>
          <div style={tabsWrapStyle}>
            <LanguageTabs
              theme={theme}
              value={nameLang}
              onChange={setNameLang}
              options={[
                { value: 'en', label: 'EN' },
                { value: 'es', label: 'ES' },
              ]}
              aria-label={t('courses.create.lesson_name_language_aria')}
            />
          </div>
          <input
            type="text"
            value={name[nameLang]}
            onChange={e => handleNameChange(nameLang, e.target.value)}
            placeholder={t('courses.create.lesson_name_placeholder')}
            style={inputStyle}
            aria-label={t('courses.create.lesson_name_language_aria')}
          />

          <div style={labelRowStyle}>
            <span style={labelStyle}>
              {t('courses.create.vimeo_video_url')}
            </span>
            <span style={requiredAsteriskStyle}>*</span>
          </div>
          <input
            type="url"
            value={vimeoUrl}
            onChange={e => setVimeoUrl(e.target.value)}
            placeholder={t('courses.create.vimeo_url_placeholder')}
            style={inputStyle}
            aria-required
          />

          <div style={previewBoxStyle}>
            {vimeoVideoId ? (
              <>
                <p style={{ ...previewTitleStyle, alignSelf: 'flex-start', marginBottom: theme.spacing.xs }}>
                  {t('courses.create.video_preview')}
                </p>
                <iframe
                  src={`https://player.vimeo.com/video/${vimeoVideoId}`}
                  title={t('courses.create.video_preview')}
                  style={previewIframeStyle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </>
            ) : (
              <>
                <img
                  src={iconVideoSmallModalCourses}
                  alt=""
                  width={48}
                  height={48}
                  style={{
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(0.5)',
                  }}
                  aria-hidden
                />
                <p style={previewTitleStyle}>
                  {t('courses.create.video_preview')}
                </p>
                <p style={previewHintStyle}>
                  {t('courses.create.video_preview_hint')}
                </p>
              </>
            )}
          </div>
        </div>

        <footer style={footerStyle}>
          <button
            type="button"
            style={cancelButtonStyle}
            onClick={onClose}
          >
            {t('courses.create.cancel')}
          </button>
          <button
            type="button"
            style={confirmButtonStyle}
            onClick={handleConfirm}
            disabled={!vimeoUrl.trim() || isSubmitting}
          >
            <img
              src={iconCheckWhiteModalCourses}
              alt=""
              width={18}
              height={18}
              style={{ objectFit: 'contain', flexShrink: 0 }}
              aria-hidden
            />
            {isSubmitting
              ? (t('courses.create.saving_draft') ?? 'Salvando...')
              : isEditMode
                ? (t('courses.create.save_lesson') ?? 'Guardar')
                : t('courses.create.add_lesson')}
          </button>
        </footer>
      </div>
    </div>
  );
}
